import React from "react";

import { ColDef, ICellRendererParams } from "ag-grid-community";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { getColumnSize } from "modules/common/services/table/size-storage";
import { textComparator } from "modules/common/services/table/comparators";
import { MainPageRequestItemType, RequestTypes, fields } from "modules/main/models/main-page-requests";
import { texts } from "modules/common/texts";
import { routes } from "modules/common/routes";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";
import styles from "./RequestList.module.scss";

/** Цвет кружка у типа запроса: по нему тип узнаётся, не читая строку */
const typeDot = (type: string) => {
  switch (type) {
    case RequestTypes.confirm:
      return styles.dotConfirm;
    case RequestTypes.agent:
      return styles.dotAgent;
    case RequestTypes.orderRemove:
      return styles.dotRemove;
    case RequestTypes.agentRequest:
      return styles.dotAgentRequest;
    case RequestTypes.planApproval:
      return styles.dotPlanApproval;
    default:
      return "";
  }
};

export const columns = (tableName: string) => {
  const columnSize = getColumnSize(tableName);

  const result: ColDef[] = [
    {
      headerName: "Тип",
      colId: "title",
      field: fields.title,
      width: columnSize("title", 240),
      sortable: true,
      resizable: false,
      comparator: textComparator,
      cellRenderer: ({ data }: ICellRendererParams) => (
        <div className={styles.type}>
          <span className={`${styles.dot} ${typeDot(data.type)}`} />
          <div className={styles.cutRow}>{data.title}</div>
        </div>
      ),
    },
    {
      headerName: "Наименование",
      colId: "targetName",
      field: fields.targetName,
      flex: 1,
      minWidth: 220,
      sortable: true,
      resizable: false,
      comparator: textComparator,
      cellRenderer: ({ data }: ICellRendererParams) => (
        <div className={styles.targetName}>
          {data.inventoryNumber && <div className={styles.inventoryNumber}>{data.inventoryNumber}</div>}
          <div className={styles.cutRow}>{data.targetName}</div>
        </div>
      ),
    },
    {
      headerName: "От кого",
      colId: "authorName",
      field: fields.authorName,
      width: columnSize("authorName", 150),
      sortable: true,
      resizable: false,
      comparator: textComparator,
    },
    {
      headerName: "Действия",
      colId: "actions",
      width: columnSize("actions", 200),
      sortable: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams) => {
        const { data } = params;
        const getContext = () => params.context as TableContext;

        // двойной клик по строке открывает объект — из зоны кнопок его гасим
        const stop = (e: React.MouseEvent) => e.stopPropagation();

        if (data.type === RequestTypes.orderRemove) {
          return (
            <div className={styles.actions} onDoubleClick={stop}>
              <PlanrButton
                type="danger"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setShowRemove(data);
                }}
              >
                {texts.remove}
              </PlanrButton>
              <PlanrButton
                type="graybtn"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setCancel(data);
                }}
              >
                Отклонить
              </PlanrButton>
            </div>
          );
        }

        if (data.type === RequestTypes.confirm) {
          return (
            <div className={styles.actions} onDoubleClick={stop}>
              <PlanrButton
                type="greenish"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setConfirmUpload(data);
                }}
              >
                Подтвердить
              </PlanrButton>
              <PlanrButton
                type="graybtn"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setRejectUpload(data);
                }}
              >
                Отклонить
              </PlanrButton>
            </div>
          );
        }

        // сдвиг в плане решают только на графике, глядя на контур предложенного места
        if (data.type === RequestTypes.planApproval) {
          return (
            <div className={styles.actions} onDoubleClick={stop}>
              <OpenPlanApproval id={data.id} />
            </div>
          );
        }

        if (data.type === RequestTypes.agent) {
          return (
            <div className={styles.actions} onDoubleClick={stop}>
              <PlanrButton
                type="greenish"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setApproveOutsourcer(data);
                }}
              >
                Одобрить
              </PlanrButton>
              <PlanrButton
                type="graybtn"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setRejectOutsourcer(data);
                }}
              >
                Отмена
              </PlanrButton>
            </div>
          );
        }

        if (data.type === RequestTypes.agentRequest) {
          return (
            <div className={styles.actions} onDoubleClick={stop}>
              <PlanrButton
                type="greenish"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setApproveAgentRequest(data);
                }}
              >
                Одобрить
              </PlanrButton>
              <PlanrButton
                type="danger"
                size="small"
                className={styles.actionButton}
                onClick={(e: React.MouseEvent) => {
                  stop(e);
                  getContext().setShowRemoveAgentRequest(data);
                }}
              >
                {texts.remove}
              </PlanrButton>
            </div>
          );
        }

        return null;
      },
    },
  ];

  return result;
};

export interface TableContext {
  setShowRemove: (request: MainPageRequestItemType) => void;
  setCancel: (request: MainPageRequestItemType) => Promise<void>;
  setConfirmUpload: (request: MainPageRequestItemType) => Promise<void>;
  setRejectUpload: (request: MainPageRequestItemType) => Promise<void>;
  setApproveOutsourcer: (request: MainPageRequestItemType) => Promise<void>;
  setRejectOutsourcer: (request: MainPageRequestItemType) => Promise<void>;
  setApproveAgentRequest: (request: MainPageRequestItemType) => Promise<void>;
  setShowRemoveAgentRequest: (request: MainPageRequestItemType) => void;
}

/** «Перейти» к запросу на согласование: плановый график, открытый на нём */
const OpenPlanApproval: React.FC<{ id: string }> = ({ id }) => {
  const navigate = useStableNavigate();

  return (
    <PlanrButton
      type="greenish"
      size="small"
      className={styles.actionButton}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`${routes.schedules.projectPlan}?approval=${id}`);
      }}
    >
      Перейти
    </PlanrButton>
  );
};
